import { useRef } from 'react';
import { m, useReducedMotion, useMotionValue, useMotionTemplate, useSpring, useScroll, useTransform } from 'framer-motion';
import HexShape from './HexShape';

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.11, delayChildren: 0.25 } },
};

const item = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
  const shouldReduce = useReducedMotion();
  const ref = useRef(null);

  const mouseX = useMotionValue(50);
  const mouseY = useMotionValue(50);
  const springX = useSpring(mouseX, { stiffness: 55, damping: 20 });
  const springY = useSpring(mouseY, { stiffness: 55, damping: 20 });
  const spotlight = useMotionTemplate`radial-gradient(ellipse 700px 520px at ${springX}% ${springY}%, oklch(0.98 0.02 145 / 0.18) 0%, transparent 68%)`;

  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });

  const h1y = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const h2y = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const h3y = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const h1rot = useTransform(scrollYProgress, [0, 1], [0, 18]);
  const h2rot = useTransform(scrollYProgress, [0, 1], [0, -12]);

  const h1x = useTransform(springX, [0, 100], [-10, 10]);
  const h2x = useTransform(springX, [0, 100], [8, -8]);
  const h3x = useTransform(springX, [0, 100], [-6, 6]);

  const handleMouseMove = (e) => {
    if (shouldReduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    mouseX.set(((e.clientX - r.left) / r.width) * 100);
    mouseY.set(((e.clientY - r.top) / r.height) * 100);
  };

  return (
    <section
      ref={ref}
      className="relative min-h-screen bg-primary overflow-hidden flex flex-col"
      aria-label="Hero"
      onMouseMove={handleMouseMove}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary-light/20 via-transparent to-primary-dark/30 pointer-events-none" />
      <m.div className="absolute inset-0 pointer-events-none" style={{ background: spotlight }} />

      <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
        <m.div
          className="absolute"
          style={{ bottom: '8%', left: '-4%', x: shouldReduce ? 0 : h1x, y: shouldReduce ? 0 : h1y, rotate: shouldReduce ? 0 : h1rot }}
        >
          <HexShape size={360} className="text-white" style={{ opacity: 0.14 }} />
        </m.div>
        <m.div
          className="absolute"
          style={{ top: '-6%', left: '28%', x: shouldReduce ? 0 : h2x, y: shouldReduce ? 0 : h2y, rotate: shouldReduce ? 0 : h2rot }}
        >
          <HexShape size={200} className="text-white" style={{ opacity: 0.12 }} />
        </m.div>
        <m.div
          className="absolute"
          style={{ top: '38%', left: '10%', x: shouldReduce ? 0 : h3x, y: shouldReduce ? 0 : h3y }}
        >
          <HexShape size={130} className="text-white" style={{ opacity: 0.10 }} />
        </m.div>
      </div>

      <div
        className="absolute right-0 top-0 bottom-0 w-1/2 flex items-center justify-center pointer-events-none select-none"
        aria-hidden="true"
      >
        <svg
          viewBox="0 0 293.81 303.11"
          className="h-[70%] w-auto opacity-[0.07]"
          preserveAspectRatio="xMidYMid meet"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M116.13,139.9l-26.91,46.6c-7.7,13.33-26.94,13.33-34.64,0L2.68,96.6c-3.57-6.19-3.57-13.81,0-20l13.45-23.3c3.57-6.19,10.18-10,17.32-10h26.91c7.15,0,13.75,3.81,17.32,10l38.45,66.6c3.57,6.19,3.57,13.81,0,20Z" fill="white"/>
          <path d="M133.45,0h26.91c7.15,0,13.75,3.81,17.32,10l13.45,23.3c3.57,6.19,3.57,13.81,0,20l-13.45,23.3c-3.57,6.19-10.18,10-17.32,10h-26.91c-7.15,0-13.75-3.81-17.32-10l-13.45-23.3c-3.57-6.19-3.57-13.81,0-20l13.45-23.3C119.71,3.81,126.31,0,133.45,0Z" fill="white"/>
          <path d="M291.13,96.6l-99.67,172.63c-.11.19-.22.39-.33.58l-13.45,23.3c-3.57,6.19-10.17,10-17.32,10h-26.9c-7.15,0-13.75-3.81-17.33-10l-13.45-23.3-13.45-23.3c-7.7-13.33,1.92-30,17.32-30h3.81c7.13,0,13.72-3.79,17.29-9.96.01-.01.02-.03.03-.04l1.91-3.3L216.13,53.3c3.58-6.18,10.18-10,17.33-10h26.9c7.15,0,13.75,3.82,17.32,10l13.45,23.3c3.58,6.19,3.58,13.82,0,20Z" fill="white"/>
        </svg>
      </div>

      <div className="relative z-10 flex-1 flex flex-col justify-center max-w-7xl mx-auto px-6 sm:px-8 pt-28 pb-24">
        <m.div
          variants={shouldReduce ? undefined : container}
          initial={shouldReduce ? false : 'hidden'}
          animate="visible"
          className="max-w-[52rem]"
        >
          <m.p
            variants={shouldReduce ? undefined : item}
            className="text-white/65 text-xs tracking-[0.2em] uppercase font-medium mb-10"
          >
            Built Around You
          </m.p>

          <m.h1
            variants={shouldReduce ? undefined : item}
            className="text-white font-extrabold leading-[0.9] tracking-tight mb-8"
            style={{ fontSize: 'clamp(3.25rem, 7.5vw, 7rem)' }}
          >
            Software built<br />
            <span className="text-white/75 italic">around you.</span>
          </m.h1>

          <m.p
            variants={shouldReduce ? undefined : item}
            className="text-white/60 leading-relaxed max-w-[34rem] mb-12"
            style={{ fontSize: 'clamp(1rem, 1.4vw, 1.15rem)' }}
          >
            We build digital products that genuinely fit the businesses they serve.
            Crafted with care, designed beautifully, built entirely around you.
          </m.p>

          <m.div
            variants={shouldReduce ? undefined : item}
            className="flex flex-wrap gap-4"
          >
            <m.a
              href="mailto:hello@vehi.systems"
              className="px-7 py-3.5 bg-white text-primary-dark font-semibold rounded-full text-sm hover:bg-surface transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              whileHover={shouldReduce ? undefined : { scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 420, damping: 28 }}
            >
              Let's talk
            </m.a>
            <m.a
              href="#about"
              className="px-7 py-3.5 border border-white/25 text-white font-medium rounded-full text-sm hover:border-white/50 hover:bg-white/10 transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              whileHover={shouldReduce ? undefined : { scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 420, damping: 28 }}
            >
              Our story
            </m.a>
          </m.div>
        </m.div>

        <m.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: shouldReduce ? 0 : 1.9, duration: 0.5 }}
          className="absolute bottom-8 left-6 sm:left-8 flex items-center gap-3 text-white/30 text-[0.7rem] tracking-widest select-none"
          aria-hidden="true"
        >
          <div className="h-px w-8 bg-white/25" />
          Scroll
        </m.div>
      </div>
    </section>
  );
}
